import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { shootService } from '../services/shootService'
import { clientService } from '../services/clientService'
import { Client, CreateShootData, CreateClientData, Shoot } from '../types'
import LocationAutocomplete from '../components/LocationAutocomplete'
import DoubleBookingModal from '../components/DoubleBookingModal'
import FuzzyClientSearch from '../components/FuzzyClientSearch'
import SmartSchedulingSuggestions from '../components/SmartSchedulingSuggestions'
import {
  CameraIcon,
  VideoCameraIcon,
  UserPlusIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const NewShootPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Check if we're editing an existing shoot or have prefilled data
  const editShoot = location.state?.editShoot as Shoot | undefined
  const prefillData = location.state?.prefillData
  const isEditing = !!editShoot
  const isPremium = user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio'
  
  // Form states
  const [shootData, setShootData] = useState<CreateShootData>({
    title: '',
    type: 'photo',
    client_id: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    notes: ''
  })
  
  const [clients, setClients] = useState<Client[]>([])
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const [newClientData, setNewClientData] = useState<CreateClientData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  })
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // Double booking states
  const [showDoubleBookingModal, setShowDoubleBookingModal] = useState(false)
  const [conflictingShoots, setConflictingShoots] = useState<Shoot[]>([])
  const [pendingSubmit, setPendingSubmit] = useState(false)

  // Smart scheduling states
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false)

  // Load clients on component mount and populate form if editing
  useEffect(() => {
    if (user?.id) {
      loadClients()
    }
  }, [user?.id])

  // Handle prefilled data from smart suggestions
  useEffect(() => {
    if (prefillData) {
      setShootData(prev => ({
        ...prev,
        date: prefillData.date || '',
        start_time: prefillData.start_time || '',
        end_time: prefillData.end_time || ''
      }))
    }
  }, [prefillData])

  // Populate form with existing shoot data when editing
  useEffect(() => {
    if (isEditing && editShoot && clients.length > 0) {
      setShootData({
        title: editShoot.title,
        type: editShoot.type,
        client_id: editShoot.client_id,
        date: editShoot.date,
        start_time: editShoot.start_time,
        end_time: editShoot.end_time,
        location: editShoot.location || '',
        notes: editShoot.notes || ''
      })
      
      // Set selected client
      const client = clients.find(c => c.id === editShoot.client_id)
      if (client) {
        setSelectedClient(client)
      }
    }
  }, [isEditing, editShoot, clients])

  const loadClients = async () => {
    try {
      const clientsData = await clientService.getClientsForUser(user!.id)
      setClients(clientsData)
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const handleShootDataChange = (field: keyof CreateShootData, value: string) => {
    setShootData(prev => ({ ...prev, [field]: value }))
  }

  const handleNewClientDataChange = (field: keyof CreateClientData, value: string) => {
    setNewClientData(prev => ({ ...prev, [field]: value }))
  }

  const handleClientSelect = (client: Client | null) => {
    setSelectedClient(client)
    setShootData(prev => ({ ...prev, client_id: client?.id || '' }))
    setShowNewClientForm(false)
  }

  const handleSmartSuggestionSelect = (suggestion: any) => {
    setShootData(prev => ({
      ...prev,
      date: suggestion.date,
      start_time: suggestion.startTime,
      end_time: suggestion.endTime
    }))
    setShowSmartSuggestions(false)
  }

  const handleCreateClient = async () => {
    if (!newClientData.name || !newClientData.email) {
      setError('Client name and email are required')
      return
    }

    try {
      setLoading(true)
      const newClient = await clientService.createClient(newClientData, user!.id)
      setClients(prev => [...prev, newClient])
      handleClientSelect(newClient)
      setNewClientData({ name: '', email: '', phone: '', company: '', notes: '' })
      setError(null)
    } catch (err) {
      setError('Failed to create client')
      console.error('Error creating client:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!shootData.title || !shootData.client_id || !shootData.date || !shootData.start_time || !shootData.end_time) {
      setError('Please fill in all required fields')
      return
    }

    // Check for double booking conflicts
    try {
      const conflicts = await shootService.checkForConflictingShoots(
        user!.id,
        shootData.date,
        shootData.start_time,
        shootData.end_time,
        isEditing ? editShoot?.id : undefined
      )

      if (conflicts.length > 0 && !pendingSubmit) {
        // Show double booking warning
        setConflictingShoots(conflicts)
        setShowDoubleBookingModal(true)
        return
      }

      // Proceed with creating/updating the shoot
      await submitShoot()
    } catch (err) {
      setError('Failed to check for conflicts')
      console.error('Error checking conflicts:', err)
    }
  }

  const submitShoot = async () => {
    try {
      setLoading(true)
      if (isEditing && editShoot) {
        await shootService.updateShoot(editShoot.id, shootData)
      } else {
        await shootService.createShoot(shootData, user!.id)
      }
      setSuccess(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (err) {
      setError(isEditing ? 'Failed to update shoot' : 'Failed to create shoot')
      console.error('Error saving shoot:', err)
    } finally {
      setLoading(false)
      setPendingSubmit(false)
    }
  }

  const handleConfirmDoubleBooking = () => {
    setShowDoubleBookingModal(false)
    setPendingSubmit(true)
    // Trigger submit again, this time it will bypass the conflict check
    submitShoot()
  }

  const handleCancelDoubleBooking = () => {
    setShowDoubleBookingModal(false)
    setConflictingShoots([])
    setPendingSubmit(false)
  }

  if (success) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Background orbs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="glass-mockup rounded-3xl p-12 text-center max-w-md"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            {isEditing ? 'Shoot Updated!' : 'Shoot Created!'}
          </h2>
          <p className="text-gray-400">
            {isEditing 
              ? 'Your shoot has been successfully updated.' 
              : 'Your shoot has been successfully scheduled and added to your calendar.'
            }
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="text-lg font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                {isEditing ? (
                  <PencilIcon className="w-6 h-6 text-white" />
                ) : (
                  <CameraIcon className="w-6 h-6 text-white" />
                )}
              </div>
              <span className="text-2xl font-bold text-white">
                {isEditing ? 'Edit' : 'New'} <span className="text-gray-400">Shoot</span>
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-3xl p-8"
        >
          <h1 className="text-3xl font-bold text-white mb-8">
            {isEditing ? 'Edit Shoot Details' : 'Schedule a New Shoot'}
          </h1>
          
          {/* Edit Mode Actions */}
          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20"
            >
              <div className="flex items-center space-x-3 mb-3">
                <PencilIcon className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Editing: {editShoot?.title}</h2>
              </div>
              <p className="text-gray-300 text-sm">
                You can update any details below. Changes will be saved when you click "Update Shoot".
              </p>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center space-x-3"
            >
              <XMarkIcon className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Shoot Type Selection */}
            <div>
              <label className="block text-white font-medium mb-4">Shoot Type</label>
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleShootDataChange('type', 'photo')}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    shootData.type === 'photo'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-white/20 bg-white/5 hover:border-white/30'
                  }`}
                >
                  <CameraIcon className="w-8 h-8 text-white mx-auto mb-3" />
                  <span className="text-white font-medium">Photo Shoot</span>
                </motion.button>
                
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleShootDataChange('type', 'video')}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    shootData.type === 'video'
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-white/20 bg-white/5 hover:border-white/30'
                  }`}
                >
                  <VideoCameraIcon className="w-8 h-8 text-white mx-auto mb-3" />
                  <span className="text-white font-medium">Video Shoot</span>
                </motion.button>
              </div>
            </div>

            {/* Shoot Title */}
            <div>
              <label className="block text-white font-medium mb-3">Shoot Title *</label>
              <input
                type="text"
                value={shootData.title}
                onChange={(e) => handleShootDataChange('title', e.target.value)}
                placeholder="e.g., Wedding Photography, Product Shoot"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                required
              />
            </div>

            {/* Client Selection */}
            <div>
              <label className="block text-white font-medium mb-3">Client *</label>
              
              {!showNewClientForm ? (
                <div className="space-y-4">
                  {selectedClient ? (
                    <div className="p-4 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{selectedClient.name}</p>
                        <p className="text-gray-400 text-sm">{selectedClient.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedClient(null)
                          setShootData(prev => ({ ...prev, client_id: '' }))
                        }}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <FuzzyClientSearch
                        clients={clients}
                        selectedClient={selectedClient}
                        onClientSelect={handleClientSelect}
                        placeholder="Search for a client..."
                      />
                      
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setShowNewClientForm(true)}
                        className="w-full p-4 rounded-xl border-2 border-dashed border-white/30 hover:border-white/50 text-white transition-all flex items-center justify-center space-x-2"
                      >
                        <UserPlusIcon className="w-5 h-5" />
                        <span>Add New Client</span>
                      </motion.button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 p-6 rounded-xl bg-white/5 border border-white/20">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-medium">New Client Information</h3>
                    <button
                      type="button"
                      onClick={() => setShowNewClientForm(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={newClientData.name}
                      onChange={(e) => handleNewClientDataChange('name', e.target.value)}
                      placeholder="Client Name *"
                      className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                      required
                    />
                    <input
                      type="email"
                      value={newClientData.email}
                      onChange={(e) => handleNewClientDataChange('email', e.target.value)}
                      placeholder="Email Address *"
                      className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                      required
                    />
                    <input
                      type="tel"
                      value={newClientData.phone}
                      onChange={(e) => handleNewClientDataChange('phone', e.target.value)}
                      placeholder="Phone Number"
                      className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                    <input
                      type="text"
                      value={newClientData.company}
                      onChange={(e) => handleNewClientDataChange('company', e.target.value)}
                      placeholder="Company"
                      className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                  
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    onClick={handleCreateClient}
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50"
                  >
                    {loading ? 'Creating Client...' : 'Create Client'}
                  </motion.button>
                </div>
              )}
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-white font-medium mb-3">Date *</label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={shootData.date}
                    onChange={(e) => handleShootDataChange('date', e.target.value)}
                    className="input-field pl-10"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-3">Start Time *</label>
                <div className="relative">
                  <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="time"
                    value={shootData.start_time}
                    onChange={(e) => handleShootDataChange('start_time', e.target.value)}
                    className="input-field pl-10"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-3">End Time *</label>
                <div className="relative">
                  <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="time"
                    value={shootData.end_time}
                    onChange={(e) => handleShootDataChange('end_time', e.target.value)}
                    className="input-field pl-10"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Smart Scheduling Button (Premium) */}
            {isPremium && (
              <div className="flex justify-center">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowSmartSuggestions(true)}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-400 rounded-xl hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                >
                  <SparklesIcon className="w-5 h-5" />
                  <span>Get Smart Time Suggestions</span>
                </motion.button>
              </div>
            )}

            {/* Location */}
            <div>
              <label className="block text-white font-medium mb-3">Location</label>
              <LocationAutocomplete
                value={shootData.location}
                onChange={(value) => handleShootDataChange('location', value)}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-white font-medium mb-3">Notes</label>
              <div className="relative">
                <DocumentTextIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  value={shootData.notes}
                  onChange={(e) => handleShootDataChange('notes', e.target.value)}
                  placeholder="Additional notes, requirements, or special instructions"
                  rows={4}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                />
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold text-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50"
            >
              {loading 
                ? (isEditing ? 'Updating Shoot...' : 'Creating Shoot...') 
                : (isEditing ? 'Update Shoot' : 'Schedule Shoot')
              }
            </motion.button>
          </form>
        </motion.div>
      </main>

      {/* Double Booking Modal */}
      <DoubleBookingModal
        isOpen={showDoubleBookingModal}
        onClose={handleCancelDoubleBooking}
        onConfirm={handleConfirmDoubleBooking}
        conflictingShoots={conflictingShoots}
        newShootData={{
          title: shootData.title,
          date: shootData.date,
          start_time: shootData.start_time,
          end_time: shootData.end_time,
          client: selectedClient ? { name: selectedClient.name } : undefined
        }}
      />

      {/* Smart Scheduling Suggestions */}
      {isPremium && (
        <SmartSchedulingSuggestions
          isOpen={showSmartSuggestions}
          onClose={() => setShowSmartSuggestions(false)}
          onSelectSuggestion={handleSmartSuggestionSelect}
        />
      )}
    </div>
  )
}

export default NewShootPage 